import React, { useCallback, useEffect, useState } from "react";
import { FiDownload, FiPlus, FiTrash2, FiUpload } from "react-icons/fi";
import Header from "../components/layout/Header";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Modal from "../components/ui/Modal";
import Select from "../components/ui/Select";
import Input from "../components/ui/Input";
import EmptyState from "../components/ui/EmptyState";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { payslipsApi, Payslip } from "../api/payslips";
import { employeesApi } from "../api/employees";
import { Employee } from "../types";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import toast from "react-hot-toast";

const PayslipsRhPage: React.FC = () => {
  const [payslips, setPayslips] = useState<Payslip[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [periodLabel, setPeriodLabel] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [payslipsRes, employeesRes] = await Promise.all([
        payslipsApi.list(),
        employeesApi.list({ status: "active", per_page: 100 }),
      ]);
      const payslipData = payslipsRes.data.data?.payslips ?? [];
      const empPayload = employeesRes.data as {
        employees?: Employee[];
        data?: Employee[];
      };
      const empList = empPayload.employees ?? empPayload.data ?? [];
      setPayslips(payslipData);
      setEmployees(empList);
    } catch {
      toast.error("Impossible de charger les bulletins de paie");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setEmployeeId("");
    setPeriodLabel("");
    setPeriodStart("");
    setPeriodEnd("");
    setTitle("");
    setFile(null);
  };

  const handleUpload = async () => {
    if (!employeeId) {
      toast.error("Sélectionnez un collaborateur");
      return;
    }
    if (!file) {
      toast.error("Sélectionnez un fichier PDF");
      return;
    }
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Seuls les fichiers PDF sont acceptés");
      return;
    }

    const formData = new FormData();
    formData.append("employee_id", employeeId);
    formData.append("file", file);
    if (title.trim()) formData.append("title", title.trim());
    if (periodLabel.trim()) formData.append("period_label", periodLabel.trim());
    if (periodStart) formData.append("period_start", periodStart);
    if (periodEnd) formData.append("period_end", periodEnd);

    setUploading(true);
    try {
      await payslipsApi.upload(formData);
      toast.success("Bulletin déposé : visible par le collaborateur");
      setShowUpload(false);
      resetForm();
      await loadData();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Échec du dépôt du bulletin";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (payslip: Payslip) => {
    try {
      const res = await payslipsApi.download(payslip.id);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = payslip.original_filename || `${payslip.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Échec du téléchargement");
    }
  };

  const handleDelete = async (payslip: Payslip) => {
    if (!window.confirm(`Supprimer le bulletin « ${payslip.title} » ?`)) {
      return;
    }
    try {
      await payslipsApi.delete(payslip.id);
      toast.success("Bulletin supprimé");
      setPayslips((prev) => prev.filter((p) => p.id !== payslip.id));
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const employeeOptions = employees.map((e) => ({
    value: String(e.id),
    label: `${e.first_name} ${e.last_name}${e.email ? ` (${e.email})` : ""}`,
  }));

  return (
    <div>
      <Header
        title="Bulletins de paie"
        subtitle="Déposez les bulletins PDF : chaque collaborateur ne voit que les siens"
      />

      <div style={{ padding: "2rem", maxWidth: 1100, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "1rem",
          }}
        >
          <Button
            icon={<FiPlus size={16} />}
            onClick={() => setShowUpload(true)}
          >
            Déposer un bulletin
          </Button>
        </div>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "3rem",
            }}
          >
            <LoadingSpinner size={36} />
          </div>
        ) : payslips.length === 0 ? (
          <Card padding="2rem">
            <EmptyState
              title="Aucun bulletin déposé"
              description="Importez les bulletins PDF de vos collaborateurs. Ils seront accessibles dans leur espace « Ma paie »."
              actionLabel="Déposer un bulletin"
              onAction={() => setShowUpload(true)}
            />
          </Card>
        ) : (
          <Card padding="0">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1.5fr 1fr 120px",
                gap: "0.5rem",
                padding: "0.75rem 1.25rem",
                background: "var(--color-bg)",
                fontSize: "var(--font-size-xs)",
                fontWeight: 600,
                color: "var(--color-text-secondary)",
              }}
            >
              <span>Bulletin</span>
              <span>Collaborateur</span>
              <span>Date de dépôt</span>
              <span style={{ textAlign: "right" }}>Actions</span>
            </div>
            {payslips.map((payslip, index) => (
              <div
                key={payslip.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.5fr 1fr 120px",
                  gap: "0.5rem",
                  alignItems: "center",
                  padding: "1rem 1.25rem",
                  borderTop:
                    index > 0
                      ? "1px solid var(--color-border-light)"
                      : undefined,
                }}
              >
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 500,
                      fontSize: "var(--font-size-sm)",
                    }}
                  >
                    {payslip.title}
                  </p>
                  {payslip.period_display && (
                    <p
                      style={{
                        margin: "0.15rem 0 0",
                        fontSize: "var(--font-size-xs)",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      {payslip.period_display}
                    </p>
                  )}
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: "var(--font-size-sm)" }}>
                    {payslip.employee_name || payslip.user_name || "-"}
                  </p>
                  {payslip.user_email && (
                    <p
                      style={{
                        margin: 0,
                        fontSize: "var(--font-size-xs)",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      {payslip.user_email}
                    </p>
                  )}
                </div>
                <span
                  style={{
                    fontSize: "var(--font-size-sm)",
                    color: "var(--color-text-secondary)",
                  }}
                >
                  {payslip.created_at
                    ? format(new Date(payslip.created_at), "d MMM yyyy", {
                        locale: fr,
                      })
                    : "-"}
                </span>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "0.35rem",
                  }}
                >
                  <button
                    type="button"
                    title="Télécharger"
                    onClick={() => handleDownload(payslip)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--color-brand)",
                      cursor: "pointer",
                      padding: "0.25rem",
                    }}
                  >
                    <FiDownload size={18} />
                  </button>
                  <button
                    type="button"
                    title="Supprimer"
                    onClick={() => handleDelete(payslip)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--color-danger, #E74C3C)",
                      cursor: "pointer",
                      padding: "0.25rem",
                    }}
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </Card>
        )}

        <p
          style={{
            marginTop: "1.25rem",
            fontSize: "var(--font-size-xs)",
            color: "var(--color-text-muted)",
            lineHeight: 1.6,
          }}
        >
          Le collaborateur doit disposer d&apos;un compte JULIA dont
          l&apos;email correspond à celui du registre du personnel.
        </p>
      </div>

      <Modal
        isOpen={showUpload}
        onClose={() => {
          setShowUpload(false);
          resetForm();
        }}
        title="Déposer un bulletin de paie"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setShowUpload(false);
                resetForm();
              }}
            >
              Annuler
            </Button>
            <Button
              icon={<FiUpload size={16} />}
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? "Dépôt en cours…" : "Déposer"}
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Select
            label="Collaborateur"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            options={[
              { value: "", label: "Sélectionner un collaborateur" },
              ...employeeOptions,
            ]}
            required
          />
          <Input
            label="Libellé de période (ex. Juin 2026)"
            value={periodLabel}
            onChange={(e) => setPeriodLabel(e.target.value)}
            placeholder="Juin 2026"
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0.75rem",
            }}
          >
            <Input
              label="Début de période"
              type="date"
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
            <Input
              label="Fin de période"
              type="date"
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
            />
          </div>
          <Input
            label="Titre (optionnel)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Généré automatiquement si vide"
          />
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.35rem",
                fontSize: "var(--font-size-sm)",
                fontWeight: 500,
              }}
            >
              Fichier PDF
            </label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              style={{ fontSize: "var(--font-size-sm)" }}
            />
            {file && (
              <p
                style={{
                  margin: "0.35rem 0 0",
                  fontSize: "var(--font-size-xs)",
                  color: "var(--color-text-muted)",
                }}
              >
                {file.name} ({(file.size / 1024).toFixed(0)} Ko)
              </p>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PayslipsRhPage;
